/**
 * Флоу «Подготовка хозяйства» — данные и вся арифметика шагов (AK-772).
 *
 * Wireframe v3, одобрен founder 31.08:
 * `docs/superpowers/prototypes/season-planning-2027/10-farm-card-onboarding.html`.
 * Решение founder дословно: «надо разделить на две группы: 1) флоу подготовки
 * хозяйства к использованию — поля правильные, история правильная, имена полей
 * правильные, техника в хозяйствах правильная; 2) флоу планирования бюджета».
 * Плюс третий флоу-петля: «возврат к онбордингу — добавление/архивация техники
 * и полей». Поэтому экран не закрывается после первого прохода, а меняет тон.
 *
 * До AK-900 счёт «N из 3» жил ЗДЕСЬ, а не на сервере: два шага считала ручка
 * `GET /farms/{id}/preparation`, третий (парк техники) — штатные ручки парка,
 * потому что парк гейтится правом `company_reference`, которого у ручки
 * подготовки не было. Эта склейка (`buildFarmSteps`) осталась как FALLBACK для
 * старого бэкенда — см. ниже.
 *
 * AK-899: отдельного экрана подготовки больше нет — шаги стали полосой
 * готовности в шапке «Данных хозяйства». Список шагов идёт из ручки, а не из
 * фронта.
 *
 * AK-900: ручка отдаёт шесть шагов (Поля · История культур · Техника ·
 * Механизаторы · Агрономы и поля · План сезона) с полями `done`, `count`,
 * `total`, `href_hint`, `state` и итогом `done_count` / `total_steps`. Технику
 * теперь считает СЕРВЕР с тем же право-гейтом, что ручки парка (без права —
 * `state=no_access`, число не отдаётся). Полоса — `FarmTwinStrip`
 * (`components/farm/`, тот же компонент у мастера онбординга AK-856); её вид
 * собирает `buildTwinView`: новый договор → шаги с сервера как есть, старый
 * (нет `state` / `done_count`) → `buildFarmSteps` с клиентской техникой, чтобы
 * UI можно было выкатить до/вместе с серверной частью без падения.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { TwinStep, TwinStepState, TwinTone } from '@/components/farm/FarmTwinStrip';
import { api } from '@/lib/api';
import { formatRuDateNumeric, pluralRu } from '@/lib/dateFmt';
import {
  fleetFreshnessLabel,
  machineryPlural,
  FLEET_STALE_AFTER_DAYS,
  MACHINERY_FORMS,
  type FleetFreshness,
  type MachineryCounts,
} from '@/pages/structure/farm-card/machinery/machineryLogic';

/** Светофор шага. `unknown` — «сказать нечем», это НЕ «плохо». */
export type PrepStatus = 'ok' | 'warn' | 'alert' | 'unknown';

export interface PrepReason {
  code: string;
  label: string;
  count: number;
  severity: 'ok' | 'warn' | 'alert';
  /** Значение `issue` списка полей карточки — адрес причины. */
  issue: string | null;
}

export interface PrepStep {
  /**
   * Ключ шага. Сейчас ручка отдаёт `fields` и `history`; список растёт на
   * сервере (AK-900: механизаторы, агрономы и поля, план сезона), и полоса
   * обязана показать новый шаг без правки фронта — поэтому строка, а не
   * закрытое объединение.
   */
  key: string;
  title: string;
  status: 'ok' | 'warn' | 'alert';
  remaining: number;
  reasons: PrepReason[];
  // ── AK-900: поля шести шагов. Необязательные: старый бэкенд их не отдаёт,
  //    и полоса обязана работать на старых ключах (fallback в `buildTwinView`).
  /** Шаг закрыт. Не то же, что `status === 'ok'` у пустого хозяйства. */
  done?: boolean;
  /** Число как во вкладке. `null` — числа нет (нет источника / доступа), не ноль. */
  count?: number | null;
  /** Знаменатель, если у шага он есть («клеток истории», «полей хозяйства»). */
  total?: number | null;
  /** Относительный адрес приложения: `/farm/<id>?tab=…` или планирование. */
  href_hint?: string;
  /** Про ДАННЫЕ шага: есть / нет / источника ещё нет / нет права читать. */
  state?: TwinStepState;
}

export interface PrepHistory {
  years_from: number;
  years_to: number;
  fields_total: number;
  cells_total: number;
  cells_filled: number;
  fields_without_history: number;
}

export interface FarmPreparation {
  farm_id: string;
  farm_name: string;
  cluster_name: string | null;
  computed_at: string;
  last_data_change_at: string | null;
  fields_active: number;
  arable_ha: number;
  history: PrepHistory;
  steps: PrepStep[];
  /** AK-900: «заполнен N из 6» считает сервер — полоса не складывает шаги сама. */
  done_count?: number;
  total_steps?: number;
}

export const farmPrepKeys = {
  /**
   * Ключ живёт ПОД префиксом карточки `['farm-card', <id>]` (см.
   * `farmCardKeys.all` в `pages/structure/farm-card/queries.ts`), а не рядом с
   * ним: любая мутация карточки — архивация полей, отделения, импорт и
   * списание техники — инвалидирует весь префикс, и полоса двойника
   * обновляется вместе с вкладками без отдельной строки в каждой мутации.
   * Пока технику считал клиент, парк попадал в полосу через ключи парка; теперь
   * её считает ручка подготовки (AK-900), и без этого префикса импорт техники
   * оставлял бы полосу стоять. Литерал, а не импорт `farmCardKeys`: модуль
   * карточки уже импортирует этот — был бы цикл.
   */
  farm: (farmId: string | null) => ['farm-card', farmId ?? 'none', 'preparation'] as const,
};

export function useFarmPreparation(farmId: string | null): UseQueryResult<FarmPreparation> {
  return useQuery({
    queryKey: farmPrepKeys.farm(farmId),
    queryFn: () => api.get<FarmPreparation>(`/api/v1/farms/${farmId}/preparation`),
    enabled: !!farmId,
    staleTime: 30_000,
  });
}

// ─── Склейка шагов ──────────────────────────────────────────────────────────

/** Шаг флоу так, как его показывает экран. */
export interface FarmStepView {
  /** Ключ шага ручки подготовки; `machinery` считается на клиенте (см. шапку). */
  key: string;
  /** Порядковый номер — рекомендация, а не замок: шаги не заперты друг за другом. */
  order: number;
  title: string;
  status: PrepStatus;
  /** «осталось разобрать 85» / «разобрано» — одна строка, а не процент. */
  remainingText: string;
  /** Сколько единиц работы осталось числом — для адресных фраз финала. */
  remaining: number;
  /** Что именно не так, языком агронома. */
  summary: string;
  /** Причины разбора (только у шага «Поля»). */
  reasons: PrepReason[];
}

export interface FarmFlowView {
  steps: FarmStepView[];
  stepsTotal: number;
  stepsDone: number;
  /** Все шаги зелёные — разбирать нечего. */
  ready: boolean;
  /** Дата последней правки данных человеком. `null` — правок не было. */
  lastUpdateAt: string | null;
  /** Хоть один шаг не смог посчитаться (нет доступа к парку). */
  hasUnknown: boolean;
}

const FIELD_FORMS = ['поле', 'поля', 'полей'] as const;
const CELL_FORMS = ['клетка', 'клетки', 'клеток'] as const;

/**
 * Короткие имена шагов для полосы — те же слова, что у вкладок раздела
 * (макет v2: «Поля · История культур · Техника»). Шаг, которого здесь нет,
 * берёт заголовок из ручки: новый шаг сервера виден сразу, а не после
 * правки этого словаря.
 */
const STEP_TITLE: Record<string, string> = {
  fields: 'Поля',
  history: 'История культур',
  machinery: 'Техника',
};

/** «осталось разобрать N» — счётчик убывает на глазах, процент так не умеет. */
function remainingText(count: number, forms: readonly [string, string, string]): string {
  if (count === 0) return 'разобрано';
  return `осталось разобрать ${count} ${pluralRu(count, forms)}`;
}

function fieldsSummary(step: PrepStep, prep: FarmPreparation): string {
  if (step.remaining === 0) {
    return `${prep.fields_active} ${pluralRu(prep.fields_active, FIELD_FORMS)} · контуры, площади и имена без сигналов`;
  }
  const named = step.reasons
    .filter((r) => r.count > 0)
    .map((r) => `${r.label.split(' —')[0].toLowerCase()} ${r.count}`)
    .join(' · ');
  return named || 'есть поля, которые надо разобрать';
}

function historySummary(h: PrepHistory): string {
  if (h.fields_total === 0) return 'Пашни в хозяйстве пока нет — историю вести не по чему';
  const pct = h.cells_total ? Math.round((h.cells_filled / h.cells_total) * 100) : 0;
  const tail = h.fields_without_history
    ? ` · у ${h.fields_without_history} ${pluralRu(h.fields_without_history, FIELD_FORMS)} истории нет вовсе`
    : '';
  return `История посевов есть на ${pct} % клеток за ${h.years_from}–${h.years_to}${tail}`;
}

function machineryStep(
  counts: MachineryCounts | undefined,
  freshness: FleetFreshness | undefined,
  denied: boolean,
): FarmStepView {
  const base: Pick<FarmStepView, 'key' | 'order' | 'title' | 'reasons'> = {
    key: 'machinery',
    // Номер проставляет `buildFarmSteps` по месту шага в списке.
    order: 0,
    title: STEP_TITLE.machinery,
    reasons: [],
  };
  if (denied) {
    return {
      ...base,
      status: 'unknown',
      remaining: 0,
      remainingText: 'нет доступа',
      summary: 'Парк техники ведут в справочниках компании — попросите доступ у администратора',
    };
  }
  if (!counts) {
    return {
      ...base,
      status: 'unknown',
      remaining: 0,
      remainingText: 'считаем…',
      summary: 'Смотрим парк хозяйства',
    };
  }
  if (counts.total === 0) {
    return {
      ...base,
      status: 'alert',
      remaining: 0,
      remainingText: 'осталось разобрать всё',
      summary: 'В парке нет ни одной единицы — система не знает, чем хозяйство работает в поле',
    };
  }
  const label = fleetFreshnessLabel(freshness);
  if (label.stale) {
    return {
      ...base,
      status: 'warn',
      remaining: 0,
      remainingText: 'пора подтвердить',
      // Не блокирует и не сбрасывает готовность в ноль — просто честная
      // пометка, что данные могли устареть (решение founder 28.08).
      summary: `${machineryPlural(counts.total)} · парк не проверялся больше ${FLEET_STALE_AFTER_DAYS} дней — подтвердите или поправьте`,
    };
  }
  return {
    ...base,
    status: 'ok',
    remaining: 0,
    remainingText: 'разобрано',
    summary: `${machineryPlural(counts.total)} · ${label.text.replace('Актуальность парка: ', '')}`,
  };
}

/** Шаг, который считает ручка подготовки (всё, кроме техники). */
function serverStep(step: PrepStep, prep: FarmPreparation): FarmStepView {
  const base = {
    key: step.key,
    order: 0,
    title: STEP_TITLE[step.key] ?? step.title,
    status: step.status,
    remaining: step.remaining,
    reasons: step.reasons,
  };
  if (step.key === 'fields') {
    return {
      ...base,
      remainingText: remainingText(step.remaining, FIELD_FORMS),
      summary: fieldsSummary(step, prep),
    };
  }
  if (step.key === 'history') {
    return {
      ...base,
      remainingText: remainingText(step.remaining, CELL_FORMS),
      summary: historySummary(prep.history),
      reasons: [],
    };
  }
  // Шаг, о котором фронт ещё не знает: единицу счёта называет только сервер,
  // поэтому без существительного — но счётчик и цвет честные.
  return {
    ...base,
    remainingText: step.remaining === 0 ? 'разобрано' : `осталось разобрать ${step.remaining}`,
    summary: '',
  };
}

/**
 * Шаги готовности из двух источников + общий счёт.
 *
 * Список и порядок шагов задаёт ручка подготовки. Технику ручка не считает
 * (право `company_reference`, см. шапку файла), поэтому её шаг клиентский:
 * если сервер поставил в списке место `machinery` — шаг встаёт туда, иначе
 * идёт последним.
 *
 * Чистая функция без запросов и без разметки: цвет шага, счётчик «осталось
 * разобрать» и вердикт «готово / не готово» — ровно те свойства, которые ломают
 * незаметно, поэтому они проверяются тестом, а не глазами.
 */
export function buildFarmSteps(input: {
  prep: FarmPreparation | undefined;
  counts: MachineryCounts | undefined;
  freshness: FleetFreshness | undefined;
  machineryDenied?: boolean;
}): FarmFlowView {
  const { prep, counts, freshness, machineryDenied = false } = input;
  const machinery = machineryStep(counts, freshness, machineryDenied);
  if (!prep) {
    return {
      steps: [{ ...machinery, order: 1 }],
      stepsTotal: 1,
      stepsDone: 0,
      ready: false,
      lastUpdateAt: null,
      hasUnknown: true,
    };
  }
  const ordered = prep.steps.map((s) => (s.key === 'machinery' ? machinery : serverStep(s, prep)));
  if (!ordered.some((s) => s.key === 'machinery')) ordered.push(machinery);
  const steps = ordered.map((s, i) => ({ ...s, order: i + 1 }));
  const dates = [prep.last_data_change_at, freshness?.fresh_as_of ?? null].filter(
    (d): d is string => !!d,
  );
  return {
    steps,
    stepsTotal: steps.length,
    stepsDone: steps.filter((s) => s.status === 'ok').length,
    // «Готово» — только когда зелёные ВСЕ. Жёлтый шаг это ещё работа, а не
    // готовность: иначе полоса рапортовала бы «всё в порядке» поверх
    // незакрытого шага.
    ready: steps.every((s) => s.status === 'ok'),
    // ISO-строки сравнимы лексикографически — отдельного парса дат не нужно.
    lastUpdateAt: dates.length ? [...dates].sort()[dates.length - 1] : null,
    hasUnknown: steps.some((s) => s.status === 'unknown'),
  };
}

/** «последняя актуализация 31.08.2026» — без даты фразы не бывает. */
export function lastUpdateText(iso: string | null): string {
  return iso ? `последняя актуализация ${formatRuDateNumeric(iso)}` : 'правок пока не было';
}

// ─── Полоса двойника (AK-900) ───────────────────────────────────────────────

/** Что полоса получает от страницы: шаги + итог + даты + откуда взято. */
export interface FarmTwinView {
  steps: TwinStep[];
  doneCount: number;
  totalSteps: number;
  /** Все шаги закрыты — полоса сворачивается в строку. */
  complete: boolean;
  /**
   * Время расчёта ручкой (`computed_at`). Сервер считает на каждый запрос, так
   * что это всегда «сейчас» — в подписях не показывается (ревью head-web
   * #1112), оставлено для отладки и будущих потребителей.
   */
  checkedAt: string | null;
  /**
   * Последняя правка данных человеком (`last_data_change_at`) — «данные от
   * дд.мм.гггг» в свёрнутой строке полосы и в подписи «последняя актуализация».
   */
  lastUpdateAt: string | null;
  /** `server` — шесть шагов с сервера; `legacy` — старый договор, техника на клиенте. */
  source: 'server' | 'legacy';
}

/**
 * Новый договор ручки (AK-900): у КАЖДОГО шага есть `state`, у ответа —
 * `done_count` / `total_steps`. Проверяем все шаги, а не первый: смешанный
 * ответ не бывает, но половинчатый договор лучше честно считать старым, чем
 * рисовать «6 из undefined».
 */
export function isTwinContract(prep: FarmPreparation): boolean {
  return (
    typeof prep.done_count === 'number' &&
    typeof prep.total_steps === 'number' &&
    prep.steps.length > 0 &&
    prep.steps.every((s) => typeof s.state === 'string')
  );
}

/**
 * Единицы счёта для подписи «заполнено · 412 полей». Только существительное —
 * название шага и само число приходят с сервера. Шаг без строки здесь
 * показывает число голым: новый серверный шаг виден сразу, слово добавят потом.
 */
const UNIT_FORMS: Record<string, readonly [string, string, string]> = {
  fields: FIELD_FORMS,
  history: CELL_FORMS,
  machinery: MACHINERY_FORMS,
  mechanizers: ['человек', 'человека', 'человек'],
  agronomists: FIELD_FORMS,
  season_plan: ['вариант', 'варианта', 'вариантов'],
};

function withUnit(key: string, n: number): string {
  const forms = UNIT_FORMS[key];
  return forms ? `${n} ${pluralRu(n, forms)}` : String(n);
}

/**
 * Существительное после «из N» — родительный падеж множественного числа
 * («из 2884 клеток», «из 3 полей»), а не согласование с числом как в
 * `pluralRu` («2884 клетки»). У чисел на 1 (кроме 11) нужен родительный
 * единственного («из 21 поля»), которого в тройке форм нет — там слово
 * опускаем: «0 из 21» честнее, чем «из 21 поле».
 */
function afterIz(key: string, n: number): string {
  const forms = UNIT_FORMS[key];
  if (!forms || (n % 10 === 1 && n % 100 !== 11)) return String(n);
  return `${n} ${forms[2]}`;
}

/** «2752 из 2884 клеток» / «42 единицы» / null, если числа нет. */
function countText(step: Pick<PrepStep, 'key' | 'count' | 'total'>): string | null {
  const count = step.count ?? null;
  const total = step.total ?? null;
  if (total !== null) return `${count ?? 0} из ${afterIz(step.key, total)}`;
  if (count !== null) return withUnit(step.key, count);
  return null;
}

function statusTone(status: PrepStep['status']): TwinTone {
  return status === 'ok' ? 'ok' : status;
}

/** Шаг нового договора — цифры, статус и адрес берутся с сервера как есть. */
function serverTwinStep(step: PrepStep, prep: FarmPreparation): TwinStep {
  const base = {
    key: step.key,
    title: step.title,
    state: step.state ?? 'empty',
    done: step.done ?? false,
    href: step.href_hint || null,
    count: step.count ?? null,
    total: step.total ?? null,
  };
  switch (base.state) {
    case 'not_available_yet':
      // Источника у шага ещё нет (ADR-082): это не ноль и не красный.
      return { ...base, tone: 'muted', label: 'появится после настройки людей', href: null };
    case 'no_access':
      // Число сервер не отдал — и мы не выдумываем ни его, ни цвет.
      return { ...base, tone: 'muted', label: 'нет доступа к разделу', count: null, total: null };
    case 'empty': {
      const text = base.total !== null ? countText(step) : null;
      return {
        ...base,
        tone: step.status === 'alert' ? 'alert' : 'warn',
        label: text ? `не заполнено · ${text}` : 'не заполнено',
      };
    }
    default: {
      if (base.done) {
        const text = countText(step);
        return { ...base, tone: 'ok', label: text ? `заполнено · ${text}` : 'заполнено' };
      }
      // Данные есть, но есть сигналы — счётчик «осталось разобрать» убывает на
      // глазах, как и раньше.
      const forms = UNIT_FORMS[step.key];
      const label = forms
        ? remainingText(step.remaining, forms)
        : `осталось разобрать ${step.remaining}`;
      const detail =
        step.key === 'fields'
          ? fieldsSummary(step, prep)
          : step.key === 'history'
            ? historySummary(prep.history)
            : '';
      return { ...base, tone: statusTone(step.status), label, detail };
    }
  }
}

/** Старый договор: вкладка по ключу — те три шага, что были в AK-899. */
const LEGACY_TAB: Record<string, string> = {
  fields: 'fields',
  history: 'history',
  machinery: 'machinery',
};

/** Шаг старой склейки в виде шага полосы — тот же компонент, те же подписи. */
function legacyTwinStep(step: FarmStepView, farmId: string, machineryDenied: boolean): TwinStep {
  const tab = LEGACY_TAB[step.key];
  const base = {
    key: step.key,
    title: step.title,
    href: tab ? `/farm/${farmId}?tab=${tab}` : null,
    count: null,
    total: null,
    detail: step.summary,
  };
  if (step.status === 'unknown') {
    const denied = step.key === 'machinery' && machineryDenied;
    return {
      ...base,
      state: denied ? 'no_access' : 'empty',
      done: false,
      tone: 'muted',
      label: denied ? 'нет доступа к разделу' : step.remainingText,
    };
  }
  if (step.status === 'ok') {
    return { ...base, state: 'ok', done: true, tone: 'ok', label: 'заполнено' };
  }
  return {
    ...base,
    state: step.remaining > 0 ? 'ok' : 'empty',
    done: false,
    tone: step.status,
    label: step.remaining > 0 ? step.remainingText : 'не заполнено',
  };
}

/**
 * Вид полосы двойника из ответа ручки.
 *
 * Новый договор (AK-900) — шаги, порядок, названия, цифры и `done_count` берутся
 * с сервера КАК ЕСТЬ: полоса ничего не складывает и не считает, иначе её цифра
 * могла бы разойтись с цифрой во вкладке. `counts` / `freshness` при этом не
 * читаются вовсе.
 *
 * Старый договор — прежняя склейка `buildFarmSteps` (техника на клиенте), чтобы
 * UI не падал на бэкенде без шести шагов.
 */
export function buildTwinView(input: {
  prep: FarmPreparation | undefined;
  counts: MachineryCounts | undefined;
  freshness: FleetFreshness | undefined;
  machineryDenied?: boolean;
}): FarmTwinView {
  const { prep, machineryDenied = false } = input;
  if (prep && isTwinContract(prep)) {
    const steps = prep.steps.map((s) => serverTwinStep(s, prep));
    const totalSteps = prep.total_steps as number;
    const doneCount = prep.done_count as number;
    return {
      steps,
      doneCount,
      totalSteps,
      complete: totalSteps > 0 && doneCount >= totalSteps,
      checkedAt: prep.computed_at,
      lastUpdateAt: prep.last_data_change_at,
      source: 'server',
    };
  }
  const flow = buildFarmSteps(input);
  return {
    steps: flow.steps.map((s) => legacyTwinStep(s, prep?.farm_id ?? '', machineryDenied)),
    doneCount: flow.stepsDone,
    totalSteps: flow.stepsTotal,
    complete: flow.ready,
    checkedAt: prep?.computed_at ?? null,
    lastUpdateAt: flow.lastUpdateAt,
    source: 'legacy',
  };
}

/**
 * Если `href` шага — вкладка ЭТОГО же хозяйства (`/farm/<id>?tab=X`), вернуть
 * имя вкладки: страница переключит её без перезагрузки. Иначе `null` —
 * адрес внешний для раздела (планирование), идти по нему как по ссылке.
 */
export function farmTabFromHref(href: string, farmId: string): string | null {
  const m = /^\/farm\/([^/?#]+)\?tab=([a-z_]+)(?:&|$)/.exec(href);
  if (!m || m[1] !== farmId) return null;
  return m[2];
}
